import { useState } from "react";
import Select, { GroupBase, StylesConfig } from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { useIntl } from "react-intl";
import { FILTER_COLORS, FILTER_SETTINGS } from "constants/constants";
import { DEBOUNCE_TIME } from "pages/admin-app/adminapp.constants";
import BaseFiltersStyles from "./BaseFiltersStyles";

export interface FilterOption {
  value: string;
  label: string;
}

export interface AdditionalFilterOptions {
  limit: number;
  afterKey: string | undefined;
}

export interface FetchOptionsParams {
  field: string;
  subCategoryQuery: string;
  limit: number;
  afterKey: string | undefined;
}

export interface FetchOptionsResponse {
  options: FilterOption[];
  hasMore: boolean;
  afterKey: string | undefined;
}

interface Props {
  onFilterChange: (
    mainFilter: FilterOption | null,
    subcategory: FilterOption | null
  ) => void;
  onRemoveFilter: (filterKey: string) => void;
  mainFilterOptions: FilterOption[];
  fetchOptions: (params: FetchOptionsParams) => Promise<FetchOptionsResponse>;
  filterLabel: string;
  selectCategoryPlaceholder: string;
  subcategoryLabel?: string;
}

const BaseFilter = ({
  onFilterChange,
  mainFilterOptions,
  fetchOptions,
  filterLabel,
  selectCategoryPlaceholder,
  subcategoryLabel,
}: Props) => {
  const intl = useIntl();

  const [selectedMainFilter, setSelectedMainFilter] =
    useState<FilterOption | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] =
    useState<FilterOption | null>(null);
  const [asyncAdditional, setAsyncAdditional] = useState({
    limit: 0,
    afterKey: null,
  });
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);

  const handleMainFilterChange = (option: FilterOption | null) => {
    setSelectedMainFilter(option);
    setSelectedSubcategory(null);
    setAsyncAdditional({ limit: FILTER_SETTINGS.LIMIT, afterKey: null });
  };

  const handleSubcategoryChange = (option: FilterOption | null) => {
    setSelectedSubcategory(option);
    if (selectedMainFilter && option) {
      onFilterChange(selectedMainFilter, option);
      setSelectedMainFilter(null);
      setSelectedSubcategory(null);
    }
  };

  const loadSubcategoryOptions = async (
    search: string,
    _loadedOptions: FilterOption[],
    additional: unknown
  ) => {
    setIsLoadingOptions(true);
    const additionalFilterOptions = additional as AdditionalFilterOptions;
    if (!selectedMainFilter) {
      return {
        options: [],
        hasMore: false,
        additional: additionalFilterOptions,
      };
    }
    try {
      const response = await fetchOptions({
        field: selectedMainFilter.value,
        subCategoryQuery: search,
        limit: additionalFilterOptions.limit,
        afterKey: additionalFilterOptions.afterKey,
      });

      // Transform response options if the main filter is "Automated RG" and convert boolean values to "Yes"/"No" for labels and 1/0 for values to be sent to the BE
      const booleanResponse =
        selectedMainFilter.label ===
        intl.formatMessage({
          id: "usAdmin.automatedRg",
        })
          ? response.options.map((option) => ({
              ...option,
              label: option.label
                ? intl.formatMessage({ id: "customComponents.yes" })
                : intl.formatMessage({ id: "customComponents.no" }),
              value: option.value ? 1 : 0,
            }))
          : response.options;

      return {
        options: booleanResponse,
        hasMore: response.hasMore,
        additional: { ...additionalFilterOptions, afterKey: response.afterKey },
      };
    } catch (error) {
      console.error("Error loading subcategory options:", error);
      return {
        options: [],
        hasMore: false,
        additional: additionalFilterOptions,
      };
    } finally {
      setIsLoadingOptions(false);
    }
  };

  const finalSubcategoryLabel =
    subcategoryLabel || intl.formatMessage({ id: "usAdmin.subcategory" });
  const finalSelectSubcategoryPlaceholder = selectedMainFilter
    ? intl.formatMessage({ id: "usAdmin.selectSubcategory" })
    : intl.formatMessage({ id: "usAdmin.selectCategoryFirst" });

  function dynamicStyles(
    baseStyles: StylesConfig<FilterOption, boolean, GroupBase<FilterOption>>
  ): StylesConfig<FilterOption, boolean, GroupBase<FilterOption>> {
    return {
      ...baseStyles,
      control: (provided, state) => ({
        ...(baseStyles.control
          ? baseStyles.control(provided, state)
          : provided),
        backgroundColor: isLoadingOptions
          ? FILTER_COLORS.BACKGROUND
          : provided.backgroundColor,
        cursor: isLoadingOptions ? "wait" : "default",
      }),
      loadingIndicator: (provided) => ({
        ...provided,
        color: FILTER_COLORS.LOADING_INDICATOR,
      }),
    };
  }

  return (
    <div className="usadmin-filter__container">
      <div className="usadmin-filter__select_wrapper">
        <div className="form_control usadmin-dropdowns">
          <label>{filterLabel}</label>
          <Select<FilterOption, false, GroupBase<FilterOption>>
            value={selectedMainFilter}
            onChange={handleMainFilterChange}
            options={mainFilterOptions}
            placeholder={selectCategoryPlaceholder}
            styles={BaseFiltersStyles}
          />
        </div>
        <div className="form_control usadmin-dropdowns">
          <label>{finalSubcategoryLabel}</label>
          <AsyncPaginate
            key={
              selectedMainFilter
                ? `subcategory-${selectedMainFilter.value}`
                : "subcategory-none"
            }
            isDisabled={!selectedMainFilter}
            value={selectedSubcategory}
            loadOptions={loadSubcategoryOptions}
            onChange={handleSubcategoryChange}
            additional={asyncAdditional}
            placeholder={finalSelectSubcategoryPlaceholder}
            debounceTimeout={DEBOUNCE_TIME}
            styles={dynamicStyles(BaseFiltersStyles)}
          />
        </div>
      </div>
    </div>
  );
};

export default BaseFilter;
